import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';

import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';
import { Label } from 'components/ui/label';
import { Textarea } from 'components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'components/ui/select';
import { cn } from 'lib/utils';
import { useTranslation } from 'react-i18next';
import { useStepper } from 'components/stepper/stepper-provider';
import { useCreateApiContext } from '../create-api-context';
import { useGetToolById } from 'features/chat/agent-chat-module/hooks/use-gpt-chat';
import { useConfigStore } from 'state/store/config/useConfigStore';
import { getToolApiConfig, normalizeToolDetails } from '../../components/tool-detail-utils';
import {
  getGraphQLOperationType,
  mergeGraphQLInputParameters,
} from './utils/graphql-utils';

const getPathFromEndpoint = (endpoint: string) => {
  if (!endpoint) return '';

  if (endpoint.startsWith('http')) {
    try {
      return new URL(endpoint).pathname;
    } catch {
      return endpoint;
    }
  }

  return endpoint;
};

const createBasicInfoSchema = (translate: (key: string) => string) =>
  z
    .object({
      name: z
        .string()
        .trim()
        .min(1, translate('NAME_REQUIRED') || 'Name is required')
        .max(64, translate('NAME_MAX_LENGTH') || 'Name must be at most 64 characters'),
      description: z
        .string()
        .trim()
        .min(1, translate('DESCRIPTION_REQUIRED') || 'Description is required')
        .max(
          3000,
          translate('DESCRIPTION_MAX_LENGTH') ||
            'Description must be at most 3000 characters'
        ),
      method: z.string().trim().min(1, translate('METHOD_REQUIRED') || 'Method is required'),
      baseUrl: z.string().trim().url(translate('INVALID_URL_FORMAT') || 'Invalid URL format'),
      // GraphQL uses the path as its operation endpoint (e.g. /graphql), same as REST.
      path: z.string().trim().optional().default(''),
      graphqlQuery: z.string().optional().default(''),
    })
    .superRefine((data, ctx) => {
      if (!data.path.trim()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['path'],
          message: translate('PATH_REQUIRED') || 'Path is required',
        });
      } else if (!data.path.startsWith('/')) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['path'],
          message: translate('PATH_MUST_START_WITH_SLASH') || 'Path must start with "/"',
        });
      }

      if (data.method === 'GRAPHQL' && !data.graphqlQuery.trim()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['graphqlQuery'],
          message: translate('GRAPHQL_QUERY_REQUIRED') || 'GraphQL query is required',
        });
      }
    });

type BasicInfoFormValues = z.infer<ReturnType<typeof createBasicInfoSchema>>;

/** Shared height and surface styles for the Path row (method, base URL, endpoint). */
const pathControlClass =
  'h-10 min-h-10 w-full rounded-md border border-input bg-background px-3 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50';

export function CreateApiBasicInfoForm({ toolId }: Readonly<{ toolId: string }>) {
  const { t } = useTranslation();
  const configurationSettings = useConfigStore((state) => state.configurationSettings);
  const projectKey = configurationSettings?.blocksKey || '';
  const { nextStep } = useStepper();
  const { formData, updateFormData } = useCreateApiContext();
  const toolQuery = useGetToolById({ toolId, projectKey }, { enabled: !!toolId && !!projectKey });
  const tool = normalizeToolDetails(toolQuery.data);
  const toolApiConfig = getToolApiConfig(tool);
  const [method, setMethod] = useState(formData.method?.toUpperCase() || 'GET');
  const baseURL = toolApiConfig?.BaseURL || '';
  const isGraphQL = method === 'GRAPHQL';

  const {
    register,
    handleSubmit,
    setValue,
    getValues,
    formState: { errors },
  } = useForm<BasicInfoFormValues>({
    resolver: zodResolver(createBasicInfoSchema(t)),
    defaultValues: {
      name: formData.name,
      description: formData.description,
      method: formData.method?.toUpperCase() || 'GET',
      baseUrl: baseURL,
      path: getPathFromEndpoint(formData.endpoint),
      graphqlQuery: formData.graphql_query || '',
    },
  });

  useEffect(() => {
    if (baseURL) {
      setValue('baseUrl', baseURL);
    }
  }, [baseURL, setValue]);

  // Prefill Input Parameters from the operation's $variables on edit/blur.
  const syncGraphQLQuery = (query: string) => {
    updateFormData({
      graphql_query: query,
      graphql_operation_type: getGraphQLOperationType(query),
      input_parameters: mergeGraphQLInputParameters(query, formData.input_parameters),
    });
  };

  const onSubmit = (data: BasicInfoFormValues) => {
    if (data.method === 'GRAPHQL') {
      const query = data.graphqlQuery || '';
      updateFormData({
        name: data.name,
        description: data.description || '',
        method: 'GRAPHQL',
        endpoint: data.path || '',
        graphql_query: query,
        graphql_operation_type: getGraphQLOperationType(query),
        input_parameters: mergeGraphQLInputParameters(query, formData.input_parameters),
      });
      nextStep();
      return;
    }

    updateFormData({
      name: data.name,
      description: data.description || '',
      method,
      endpoint: data.path || '',
      graphql_query: '',
      graphql_operation_type: undefined,
    });
    nextStep();
  };

  return (
    <div className="w-full min-w-0">
      <div className="mb-6">
        <h2 className="text-xl font-semibold sm:text-2xl">
          {t('BASIC_INFORMATION') || 'Basic Information'}
        </h2>
        <p className="mt-1 text-sm text-muted-foreground">
          {t('CREATE_API_BASIC_INFO_DESCRIPTION') ||
            'Define the core details of your API, including name, description, and path.'}
        </p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="w-full min-w-0 space-y-6">
        <div className="space-y-2">
          <Label htmlFor="name" className="font-semibold">
            {t('NAME') || 'Name'}
          </Label>
          <Input
            id="name"
            placeholder={t('TYPE_HERE') || 'Type here...'}
            maxLength={64}
            {...register('name')}
            className={errors.name ? 'border-destructive' : ''}
          />
          {errors.name ? <p className="text-sm text-destructive">{errors.name.message}</p> : null}
        </div>

        <div className="space-y-2">
          <Label htmlFor="description" className="font-semibold">
            {t('DESCRIPTION') || 'Description'}
          </Label>
          <Textarea
            id="description"
            placeholder={
              t('DESCRIPTION_MAX_LENGTH_PLACEHOLDER', {
                defaultValue:
                  'Maximum allowed length for this description is 3000 characters',
              }) || 'Maximum allowed length for this description is 3000 characters'
            }
            maxLength={3000}
            rows={4}
            {...register('description')}
            className={cn('resize-y min-h-[120px] w-full', errors.description ? 'border-destructive' : '')}
          />
          {errors.description ? <p className="text-sm text-destructive">{errors.description.message}</p> : null}
        </div>

        <div className="space-y-2">
          <Label className="font-semibold">{t('PATH') || 'Path'}</Label>
          <div className="flex min-w-0 w-full flex-col gap-3 xl:flex-row xl:items-stretch">
            <div className="w-full shrink-0 xl:w-[112px]">
              <Select
                value={method || 'GET'}
                onValueChange={(value) => {
                  setMethod(value);
                  setValue('method', value);
                  if (value === 'GRAPHQL' && !getValues('path').trim()) {
                    setValue('path', '/graphql', { shouldValidate: false });
                  }
                }}
              >
                <SelectTrigger
                  className={cn(
                    pathControlClass,
                    'flex items-center justify-between py-0 focus:ring-1 focus:ring-offset-0'
                  )}
                >
                  <SelectValue placeholder="GET" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="GET">GET</SelectItem>
                  <SelectItem value="POST">POST</SelectItem>
                  <SelectItem value="PUT">PUT</SelectItem>
                  <SelectItem value="DELETE">DELETE</SelectItem>
                  <SelectItem value="PATCH">PATCH</SelectItem>
                  <SelectItem value="GRAPHQL">{t('GRAPHQL') || 'GraphQL'}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Input
              id="baseUrl"
              type="url"
              inputMode="url"
              placeholder="https://example.com"
              autoComplete="url"
              readOnly={!!baseURL}
              {...register('baseUrl')}
              aria-label={t('BASE_URL') || 'Base URL'}
              className={cn(
                pathControlClass,
                'min-w-0 w-full py-0 xl:flex-1',
                baseURL && 'cursor-default bg-muted text-foreground',
                errors.baseUrl ? 'border-destructive' : ''
              )}
            />
            <Input
              id="path"
              placeholder={t('ENTER_PATH_STARTING_WITH_SLASH') || 'Enter path starting with "/"'}
              {...register('path')}
              autoComplete="off"
              aria-label={t('ENDPOINT_PATH') || 'Endpoint path'}
              className={cn(
                pathControlClass,
                'min-w-0 w-full py-0 xl:flex-1',
                errors.path ? 'border-destructive' : ''
              )}
            />
          </div>
          {errors.baseUrl ? <p className="text-sm text-destructive">{errors.baseUrl.message}</p> : null}
          {!errors.baseUrl && errors.path ? (
            <p className="text-sm text-destructive">{errors.path.message}</p>
          ) : null}
        </div>

        {isGraphQL ? (
          <div className="space-y-2">
            <Label htmlFor="graphqlQuery" className="font-semibold">
              {t('GRAPHQL_QUERY') || 'GraphQL Query'}
            </Label>
            <Textarea
              id="graphqlQuery"
              placeholder={'query GetUser($id: ID!) {\n  user(id: $id) {\n    id\n    name\n  }\n}'}
              rows={10}
              spellCheck={false}
              {...register('graphqlQuery', {
                onBlur: (event) => syncGraphQLQuery(event.target.value),
              })}
              className={cn(
                'resize-y min-h-[200px] w-full font-mono text-sm',
                errors.graphqlQuery ? 'border-destructive' : ''
              )}
            />
            {errors.graphqlQuery ? (
              <p className="text-sm text-destructive">{errors.graphqlQuery.message}</p>
            ) : (
              <p className="text-sm text-muted-foreground">
                {t('GRAPHQL_QUERY_HINT') ||
                  'Variables ($name) become typed input parameters; a leading "mutation" marks a write.'}
              </p>
            )}
          </div>
        ) : null}

        <div className="flex justify-start pt-4">
          <Button type="submit" className="w-full px-8 sm:w-auto">
            {t('CONTINUE') || 'Continue'}
          </Button>
        </div>
      </form>
    </div>
  );
}
